import type { PlanOffer, PlanOfferPrice } from '../../../types/catalog';
import type { OfferFeatureDescription } from '../../../types/order';

export type BillingCycle = 'month' | 'year';
export type PayChannel = 'wechat' | 'alipay' | 'bank';

/** 弹窗支付方式到下单 channel/scene。对公转账暂无对应网关。 */
export function toCheckoutPayment(channel: PayChannel): { channel: string; scene: string } | null {
  if (channel === 'wechat') return { channel: 'WECHAT', scene: 'PC_QR' };
  if (channel === 'alipay') return { channel: 'ALIPAY', scene: 'PC_QR' };
  return null;
}

export function formatMinor(amountMinor?: number, digits?: number) {
  if (typeof amountMinor !== 'number' || !Number.isFinite(amountMinor)) return '';
  const fraction = typeof digits === 'number' && digits >= 0 ? digits : 0;
  return (amountMinor / 10 ** fraction).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  });
}

export function currencySymbol(currency?: string) {
  const code = currency?.trim().toUpperCase() ?? '';
  if (code === 'CNY') return '¥';
  if (code === 'USD') return '$';
  if (code === 'VND') return '₫';
  if (code === 'THB') return '฿';
  if (code === 'IDR') return 'Rp';
  if (code === 'MYR') return 'RM';
  if (code === 'PHP') return '₱';
  if (code === 'SGD') return 'S$';
  return code;
}

export function moneyParts(price?: PlanOfferPrice, amountMinor?: number) {
  const minor = amountMinor ?? price?.amountMinor;
  const amount = formatMinor(minor, price?.currencyFractionDigits);
  if (!amount) return null;
  return {
    symbol: currencySymbol(price?.currency),
    amount,
    currency: price?.currency?.trim().toUpperCase() ?? ''
  };
}

export function originalMoneyText(price?: PlanOfferPrice) {
  if (typeof price?.listAmountMinor !== 'number' || !Number.isFinite(price.listAmountMinor)) return '';
  if (typeof price.amountMinor === 'number' && price.listAmountMinor <= price.amountMinor) return '';
  const parts = moneyParts(price, price.listAmountMinor);
  if (!parts) return '';
  return `${parts.symbol}${parts.amount}`;
}

export function isMonthPrice(days?: number) {
  return typeof days === 'number' && Number.isFinite(days) && days > 0 && days <= 45;
}

export function isYearPrice(days?: number) {
  return typeof days === 'number' && Number.isFinite(days) && days >= 300;
}

export function matchCycle(price: PlanOfferPrice, cycle: BillingCycle) {
  return cycle === 'month' ? isMonthPrice(price.durationDays) : isYearPrice(price.durationDays);
}

export function pickCyclePrice(plan: PlanOffer, cycle: BillingCycle, currency?: string) {
  const prices = plan.prices ?? [];
  const byCycle = prices.filter((item) => matchCycle(item, cycle));
  const pool = byCycle.length > 0 ? byCycle : prices;
  if (currency) {
    const matched = pool.find((item) => item.currency?.trim().toUpperCase() === currency);
    if (matched) return matched;
  }
  return pool[0];
}

export function planCurrencies(plan: PlanOffer) {
  const codes = new Set<string>();
  (plan.prices ?? []).forEach((item) => {
    const code = item.currency?.trim().toUpperCase();
    if (code) codes.add(code);
  });
  return Array.from(codes);
}

export function dailyAmountText(price?: PlanOfferPrice) {
  if (!price || typeof price.durationDays !== 'number' || price.durationDays <= 0) return '';
  if (typeof price.amountMinor !== 'number' || !Number.isFinite(price.amountMinor)) return '';
  const digits = price.currencyFractionDigits ?? 0;
  const dailyMinor = price.amountMinor / price.durationDays;
  const amount = formatMinor(dailyMinor, digits);
  if (!amount) return '';
  return `${currencySymbol(price.currency)} ${amount}`;
}

export function periodLabel(period: string | undefined, t: (key: string) => string) {
  if (period === 'DAY' || period === 'WEEK' || period === 'MONTH' || period === 'TOTAL' || period === 'NONE') {
    return t(`pricing.period_${period}`);
  }
  return period?.trim() ?? '';
}

export function featureValue(item: OfferFeatureDescription, t: (key: string) => string) {
  if (item.displayType === 'QUOTA') {
    const quota = typeof item.quota === 'number' ? String(item.quota) : '—';
    const unit = item.unit?.trim() ?? '';
    const periodRaw = item.period?.trim().toUpperCase() ?? '';
    // TOTAL / NONE 表示累计或持续额度，只展示数量+单位（如 2个），不拼「/总」。
    if (periodRaw === 'TOTAL' || periodRaw === 'NONE' || !periodRaw) {
      return `${quota}${unit}`;
    }
    const period = periodLabel(item.period, t);
    if (unit && period) return `${quota}${unit}/${period}`;
    return [quota, unit, period].filter(Boolean).join('');
  }
  if (item.displayType === 'TEXT') return item.text?.trim() || '—';
  if (item.displayType === 'BOOL') {
    if (item.supported === true) return 'check';
    if (item.supported === false) return 'cross';
  }
  return '';
}

export function durationTitle(days: number | undefined, t: (key: string, options?: Record<string, string | number>) => string) {
  if (typeof days !== 'number' || !Number.isFinite(days) || days <= 0) return '—';
  if (isMonthPrice(days)) {
    const months = Math.max(1, Math.round(days / 30));
    return t('pricing.months', { count: months });
  }
  if (isYearPrice(days)) {
    const years = Math.max(1, Math.round(days / 365));
    return years === 1 ? t('pricing.months', { count: 12 }) : t('pricing.days', { count: days });
  }
  return t('pricing.days', { count: days });
}

export function cycleUnit(days: number | undefined, t: (key: string) => string) {
  if (isMonthPrice(days)) return t('pricing.per_month');
  if (isYearPrice(days)) return t('pricing.per_year');
  return '';
}

export function planKeyOf(plan: PlanOffer, index: number) {
  return plan.planCode?.trim() || `plan-${index}`;
}

/** 基础会员不可购买：等级为 0，或名称包含「基础会员」。 */
export function isBasicPlan(plan: PlanOffer) {
  if (plan.grade === 0) return true;
  return (plan.displayName ?? '').includes('基础会员');
}

export type PlanMemberTier = 'basic' | 'professional' | 'premium';

/** 按套餐名称或等级区分基础、专业、豪华。无法识别时为空。 */
export function planMemberTier(plan: Pick<PlanOffer, 'displayName' | 'grade'>): PlanMemberTier | '' {
  const name = (plan.displayName ?? '').toLowerCase();
  if (plan.grade === 0 || name.includes('基础') || name.includes('basic')) return 'basic';
  if (name.includes('豪华') || name.includes('premium') || name.includes('deluxe')) return 'premium';
  if (name.includes('专业') || name.includes('professional') || plan.grade === 1) return 'professional';
  if (typeof plan.grade === 'number' && plan.grade >= 2) return 'premium';
  return '';
}

/** 套餐徽章。基础、专业、豪华对应静态图；无法识别时为空。 */
export function planMemberIcon(plan: PlanOffer) {
  const tier = planMemberTier(plan);
  if (tier === 'basic') return '/basic_member.png';
  if (tier === 'professional') return '/professional_member.png';
  if (tier === 'premium') return '/premium_member.png';
  return '';
}
